import { egressPolicySchema, type SettingInput, settingLoosens } from "@esi/shared";
import { Lock } from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { cn } from "@/lib/utils";
import { t as translate } from "../i18n/i18n.ts";
import { useUnsavedWork } from "../shell/session-ended.ts";
import {
  SaveState,
  type SegmentOption,
  SettingChips,
  SettingNumber,
  SettingSegmented,
  SettingSelect,
  SettingSwitch,
  SettingSwitchNumber,
} from "./controls.tsx";
import {
  type EgressAction,
  type EgressAudience,
  egressConfirmWords,
  egressLoosenSentence,
} from "./egress.ts";
import type { ProvenanceInfo } from "./ProvenanceMark.tsx";
import {
  EditorButton,
  EditorDisclosure,
  LockToggle,
  LoosenConfirm,
  RowAction,
} from "./row-actions.tsx";
import { SettingEgress } from "./SettingEgress.tsx";
import { SettingRowFrame } from "./SettingRowFrame.tsx";
import { type ControlSpec, controlOf, LOOSEN_COPY } from "./setting-controls.ts";
import { type SettingAction, useSettingSave } from "./use-setting-save.ts";

/** What an explicit editor holds while its text does not parse: nothing can be saved. */
export const INVALID = Symbol("invalid");

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** What an explicit editor's body receives. */
export interface EditorBodyProps {
  id: string;
  labelId: string;
  value: unknown;
  onChange: (value: unknown) => void;
}

/** What a surface's own editor panel receives (the classification levels' review). */
export interface EditorPanelProps {
  id: string;
  labelId: string;
  saving: boolean;
  refusal: string | null;
  /** Saves `value` with the row's lock; closes the editor once saved. */
  save: (value: unknown) => void;
  /** Forgets a refusal (an edit after it). */
  clear: () => void;
  close: () => void;
}

export interface TypedSettingRowProps {
  settingKey: string;
  input: SettingInput;
  label: string;
  help?: ReactNode;
  helpExtra?: ReactNode;
  /** The value in effect at this level. */
  value: unknown;
  /** This level's value locks the levels below. */
  locked: boolean;
  /** May change it here; otherwise the value reads in words, with no control (D4). */
  editable: boolean;
  /** May lock it for the levels below here (D3). */
  lockable: boolean;
  lockCopy: "lock" | "lockEveryTenant";
  /** Holds its own value: it can go back to the inherited one. */
  resettable: boolean;
  resetText: string;
  /** Set here and in effect (the read-only value reads in full ink). */
  setHere: boolean;
  /** Fixed above: a lock before the read-only value. */
  lockedAbove: boolean;
  marks: readonly ProvenanceInfo[];
  unmarked?: ReactNode;
  /** Reasons and dates after the marks. */
  extra?: ReactNode;
  /** The value in words. */
  describe: (value: unknown) => string;
  /** An option of a select or chips in words (default: `describe`). */
  optionLabel?: (option: string) => string;
  /** Options shown but not offered (efforts outside the allowed ones). */
  disabledOptions?: readonly string[];
  /** `tools.egress`: per audience, the actions shown but not offered (a project's, D10). */
  disabledActions?: Partial<Record<EgressAudience, readonly EgressAction[]>>;
  /** Whether a change asks first (default: `settingLoosens`, the backoffice and console list). */
  loosens?: (from: unknown, to: unknown) => boolean;
  run: (action: SettingAction) => Promise<unknown>;
  settle: () => Promise<unknown>;
  refusal: (error: Error) => string;
  /** An explicit editor's body (lists, structured values). */
  editor?: (props: EditorBodyProps) => ReactNode;
  /** A surface's own editor panel, in place of the shared form (the classification levels). */
  editorPanel?: (props: EditorPanelProps) => ReactNode;
  /** Whether an editor's draft can be saved (default: not INVALID). */
  canSave?: (draft: unknown) => boolean;
  /** Under the row, before a refusal (the scheme's read-only levels). */
  below?: ReactNode;
  /**
   * The value a reset brings back (a project's inherited value): when going back to it loosens,
   * Reset asks first (D5). Without it, Reset saves at once.
   */
  resetTo?: { value: unknown };
  /** The loosening sentence for a change to `to` (undefined: the key's backoffice sentence). */
  loosenSentence?: (to: unknown) => string | undefined;
  /** The control shows but cannot change (a project switch inherited off, D6). */
  controlDisabled?: boolean;
  /** A nullable number with no "off" here (a project's web results age, D6). */
  noOff?: boolean;
  /** Declare open editors with changes and typed numbers as unsaved work (projects, 7.5). */
  guardUnsaved?: boolean;
}

/**
 * One settings row with its typed control (openspec change settings-typed-controls): the
 * control saves in place (D2), a loosening asks first (D5), the lock and the reset sit on the
 * provenance line (D3), lists and structured values keep their editor behind a named button,
 * and rows that cannot change here read in words (D4). Shared by the backoffice and the console.
 */
export function TypedSettingRow(props: TypedSettingRowProps) {
  const { t } = useTranslation();
  const id = useId();
  const labelId = `${id}-label`;
  const sourceId = `${id}-source`;
  const confirmId = `${id}-confirm`;
  const controlRef = useRef<HTMLSpanElement>(null);
  const spec = controlOf(props.settingKey, props.input);
  const save = useSettingSave({ run: props.run, settle: props.settle });
  // A loosening waiting for its confirmation (nothing sent until then); `reset`: a Reset.
  const [asking, setAsking] = useState<{ value: unknown; reset?: true } | null>(null);
  const [editing, setEditing] = useState<{ draft: unknown } | null>(null);
  const [numberDraft, setNumberDraft] = useState(false);
  // Bumped by a Reset: the control remounts, so a typed number (or its refusal) goes.
  const [resets, setResets] = useState(0);
  const resetNow = () => {
    setResets((n) => n + 1);
    save.save({ type: "delete" });
  };
  useUnsavedWork(
    props.guardUnsaved === true &&
      ((editing !== null && !same(editing.draft, props.value)) || numberDraft),
  );
  const sentence = (to: unknown) =>
    props.loosenSentence?.(to) ??
    (spec.kind === "egress" ? egressLoosenSentence(props.value, to) : null) ??
    loosenSentence(props.settingKey);
  // The egress confirmation names the audience's actions ("Allow", "Keep blocked", D10).
  const egressWords =
    spec.kind === "egress" && asking && !asking.reset
      ? egressConfirmWords(props.value, asking.value)
      : null;
  const loosens =
    props.loosens ?? ((from: unknown, to: unknown) => settingLoosens(props.settingKey, from, to));
  const pending = save.pending?.type === "put" ? save.pending : undefined;
  const shown = pending ? pending.value : props.value;
  const lockedShown = pending ? pending.locked : props.locked;
  const refusal = save.error ? props.refusal(save.error) : null;

  const focusControl = () => {
    const root = controlRef.current;
    const el =
      root?.querySelector<HTMLElement>("[role=radio][data-state=checked]") ??
      root?.querySelector<HTMLElement>("button, input, select");
    el?.focus();
  };
  /** Focus back on an open editor's Save. */
  const focusSubmit = () =>
    controlRef.current
      ?.closest("li")
      ?.querySelector<HTMLElement>("form button[type=submit]")
      ?.focus();
  const put = (value: unknown, onSaved?: () => void) =>
    save.save({ type: "put", value, locked: props.locked }, onSaved);
  const change = (next: unknown) => {
    if (same(next, props.value)) {
      setAsking(null);
      return;
    }
    if (loosens(props.value, next)) {
      setAsking({ value: next });
      return;
    }
    setAsking(null);
    put(next);
  };

  const control = props.editable ? (
    <>
      {/* Under the egress lines (the row's width): no empty line above them while idle. */}
      <SaveState
        state={save.state}
        className={spec.kind === "egress" ? "order-last h-auto" : undefined}
      />
      <Control
        key={resets}
        spec={spec}
        props={props}
        value={shown}
        labelId={labelId}
        describedBy={asking ? `${confirmId} ${sourceId}` : sourceId}
        busy={save.busy}
        onDraft={props.guardUnsaved ? setNumberDraft : undefined}
        onChange={change}
        onOpen={() => {
          save.clear();
          setAsking(null);
          setEditing({ draft: props.value });
        }}
        editorOpen={editing !== null}
      />
    </>
  ) : (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm @xl:text-right",
        props.setHere ? "font-medium text-fg" : "text-fg-secondary",
      )}
    >
      {props.lockedAbove ? (
        <Lock aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" strokeWidth={1.75} />
      ) : null}
      {props.describe(props.value)}
    </span>
  );

  const range =
    props.input.type === "number" && (spec.kind === "number" || spec.kind === "switchNumber")
      ? rangeText(props.input.min, props.input.max, t(`settingsControls.units.${spec.unit}`))
      : null;

  const actions = (
    <>
      {props.editable && range ? <span>{range}</span> : null}
      {props.editable && props.lockable ? (
        <LockToggle
          locked={lockedShown}
          label={props.label}
          copy={props.lockCopy}
          disabled={save.busy}
          onToggle={() => {
            setAsking(null);
            save.save({ type: "put", value: props.value, locked: !props.locked });
          }}
        />
      ) : null}
      {props.resettable ? (
        <RowAction
          label={`${props.resetText}: ${props.label}`}
          disabled={save.busy}
          onClick={() => {
            // An open editor closes: Reset replaces its draft (else Save would set it again).
            setEditing(null);
            if (props.resetTo && loosens(props.value, props.resetTo.value)) {
              // The row's strip asks.
              setAsking({ value: props.resetTo.value, reset: true });
              return;
            }
            setAsking(null);
            resetNow();
          }}
        >
          {props.resetText}
        </RowAction>
      ) : null}
    </>
  );

  const canSave = (draft: unknown) =>
    draft !== INVALID && (props.canSave ? props.canSave(draft) : true);
  const close = () => {
    setEditing(null);
    setAsking(null);
  };
  const editorBelow =
    props.editable && editing ? (
      props.editorPanel ? (
        props.editorPanel({
          id,
          labelId,
          saving: save.busy,
          refusal,
          save: (value) => put(value, close),
          clear: save.clear,
          close: () => {
            save.clear();
            close();
          },
        })
      ) : (
        <EditorDisclosure
          labelledBy={labelId}
          saveDisabled={!canSave(editing.draft) || same(editing.draft, props.value)}
          saving={save.busy}
          // While the strip asks, only its Save and Keep editing act: the form's own Save and
          // Cancel step aside (one Save on screen), as a switch's strip stands alone.
          actionsHidden={asking !== null}
          onCancel={() => {
            save.clear();
            close();
          }}
          onSubmit={() => {
            const draft = editing.draft;
            if (asking || !canSave(draft) || same(draft, props.value) || save.busy) {
              return;
            }
            if (loosens(props.value, draft)) {
              setAsking({ value: draft });
            } else {
              put(draft, close);
            }
          }}
          notices={
            <>
              {refusal ? <Notice tone="danger">{refusal}</Notice> : null}
              {asking ? (
                <LoosenConfirm
                  id={confirmId}
                  label={props.label}
                  sentence={sentence(asking.value)}
                  confirm={t("settingsControls.confirm.save")}
                  keep={t("settingsControls.confirm.keepEditing")}
                  onConfirm={() => {
                    put(asking.value, () => {
                      close();
                      // Back to the row's control (its editor button) once the editor closes.
                      window.setTimeout(focusControl);
                    });
                    setAsking(null);
                    window.setTimeout(focusSubmit);
                  }}
                  onKeep={() => {
                    setAsking(null);
                    window.setTimeout(focusSubmit);
                  }}
                />
              ) : null}
            </>
          }
        >
          {props.editor?.({
            id,
            labelId,
            value: editing.draft,
            onChange: (next) => {
              save.clear();
              setAsking(null);
              setEditing({ draft: next });
            },
          })}
        </EditorDisclosure>
      )
    ) : null;

  return (
    <SettingRowFrame
      settingKey={props.settingKey}
      labelId={labelId}
      sourceId={sourceId}
      label={props.label}
      help={props.help}
      helpExtra={props.helpExtra}
      inline={props.editable && spec.kind === "switch"}
      wide={props.editable && spec.kind === "egress"}
      control={control}
      controlRef={controlRef}
      marks={props.marks}
      unmarked={props.unmarked}
      extra={
        <>
          {props.extra}
          {actions}
        </>
      }
      below={
        <>
          {props.below}
          {asking && !editing ? (
            <LoosenConfirm
              id={confirmId}
              label={props.label}
              sentence={sentence(asking.value)}
              confirm={
                spec.kind === "switch"
                  ? t("settingsControls.confirm.turnOn")
                  : asking.reset
                    ? props.resetText
                    : (egressWords?.confirm ?? t("settingsControls.confirm.allow"))
              }
              keep={
                spec.kind === "switch"
                  ? t("settingsControls.confirm.keepOff")
                  : (egressWords?.keep ??
                    (spec.kind === "egress"
                      ? t("settingsControls.egress.keepAll")
                      : t("settingsControls.confirm.keep", { value: props.describe(props.value) })))
              }
              onConfirm={() => {
                const reset = asking.reset;
                if (reset) {
                  resetNow();
                } else {
                  put(asking.value);
                }
                setAsking(null);
                // A reset remounts the control: focus it once the new one is in place.
                if (reset) {
                  window.setTimeout(focusControl);
                } else {
                  focusControl();
                }
              }}
              onKeep={() => {
                setAsking(null);
                focusControl();
              }}
            />
          ) : null}
          {editorBelow}
          {!editing && refusal ? <Notice tone="danger">{refusal}</Notice> : null}
        </>
      }
    />
  );
}

/** The effect of a loosening in one sentence (per key). */
function loosenSentence(key: string): string {
  const copy = Object.hasOwn(LOOSEN_COPY, key) ? LOOSEN_COPY[key] : undefined;
  return copy
    ? translate(`settingsControls.loosen.${copy}` as "settingsControls.loosen.webSearch")
    : translate("settingsControls.loosenGeneric");
}

/** A number's range in words, for the provenance line. */
function rangeText(min: number | null, max: number | null, unit: string): string | null {
  if (min === null && max === null) {
    return null;
  }
  return max === null
    ? translate("settingsControls.rangeHintFrom", { min: min ?? 0, unit })
    : translate("settingsControls.rangeHint", { min: min ?? 0, max, unit });
}

/** The control a spec names, for the value shown. */
function Control(p: {
  spec: ControlSpec;
  props: TypedSettingRowProps;
  value: unknown;
  labelId: string;
  describedBy: string;
  busy: boolean;
  onDraft?: (pending: boolean) => void;
  onChange: (next: unknown) => void;
  onOpen: () => void;
  editorOpen: boolean;
}) {
  const { t } = useTranslation();
  const { spec, props, value, labelId, describedBy, busy, onChange } = p;
  const common = { labelledBy: labelId, describedBy, busy, disabled: props.controlDisabled };
  const optionLabel = props.optionLabel ?? ((o: string) => props.describe(o));
  const offered = (values: readonly string[]): SegmentOption[] =>
    values.map((v) => ({
      value: v,
      label: optionLabel(v),
      disabled: props.disabledOptions?.includes(v),
    }));
  const inputOptions =
    props.input.type === "select" || props.input.type === "multiselect" ? props.input.options : [];
  switch (spec.kind) {
    case "switch":
      return <SettingSwitch {...common} checked={value === true} onChange={onChange} />;
    case "egress": {
      const policy = egressPolicySchema.safeParse(value);
      return policy.success ? (
        <SettingEgress
          {...common}
          value={policy.data}
          disabledActions={props.disabledActions}
          onChange={onChange}
        />
      ) : (
        <span className="text-sm font-medium text-fg">{props.describe(value)}</span>
      );
    }
    case "segmented": {
      const options = spec.options
        .filter((o) => inputOptions.length === 0 || inputOptions.includes(o.value))
        .map((o) => ({
          value: o.value,
          label: t(o.label),
          disabled: props.disabledOptions?.includes(o.value),
        }));
      return (
        <SettingSegmented {...common} value={String(value)} options={options} onChange={onChange} />
      );
    }
    case "select":
      return (
        <SettingSelect
          {...common}
          value={String(value)}
          options={offered(inputOptions)}
          onChange={onChange}
        />
      );
    case "chips":
      return (
        <SettingChips
          {...common}
          value={Array.isArray(value) ? (value as string[]) : []}
          options={offered(inputOptions)}
          onChange={onChange}
        />
      );
    case "number": {
      const range =
        props.input.type === "number"
          ? { min: props.input.min, max: props.input.max }
          : { min: null, max: null };
      return (
        <SettingNumber
          {...common}
          value={typeof value === "number" ? value : null}
          unit={t(`settingsControls.units.${spec.unit}`)}
          prefix={spec.prefix}
          range={range}
          onDraft={p.onDraft}
          onCommit={onChange}
        />
      );
    }
    case "switchNumber": {
      const range =
        props.input.type === "number"
          ? { min: props.input.min, max: props.input.max }
          : { min: null, max: null };
      return (
        <SettingSwitchNumber
          {...common}
          value={typeof value === "number" ? value : null}
          unit={t(`settingsControls.units.${spec.unit}`)}
          range={range}
          switchLabel={
            spec.empty === "operator"
              ? t("settingsControls.nullable.ownBudget")
              : spec.empty === "noLimit"
                ? t("settingsControls.nullable.limit")
                : t("settingsControls.on")
          }
          offText={t(`settingsControls.nullable.${spec.empty}`)}
          onValue={spec.onValue}
          noOff={props.noOff}
          onDraft={p.onDraft}
          onCommit={onChange}
        />
      );
    }
    case "text":
      // Short text has its own row (the branding name); here, the value in words.
      return <span className="text-sm font-medium text-fg">{props.describe(value)}</span>;
    case "editor":
      return (
        <>
          <span className="text-sm font-medium text-fg @xl:text-right">
            {props.describe(value)}
          </span>
          {p.editorOpen ? null : (
            <EditorButton
              action={t(`settingsControls.editors.${spec.editor}`)}
              label={props.label}
              // One write at a time: a lock or reset running holds the editor closed.
              disabled={busy}
              onOpen={p.onOpen}
            />
          )}
        </>
      );
  }
}
